import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  useGetModelesCopiesQuery,
  useCreateModeleCopieMutation,
  useUpdateModeleCopieMutation,
  useDeleteModeleCopieMutation,
  useGetLotsCopiesQuery,
  useGetCopiesExamensQuery,
  useMarquerLotImprimeMutation,
  useMarquerLotNonImprimeMutation,
  ModeleCopie,
} from '../../services/copiesApi';
import {
  Upload,
  FileImage,
  Printer,
  Trash2,
  Eye,
  Settings,
  Plus,
  X,
  Move,
  Loader2,
  CheckCircle2,
  AlertCircle,
  BookOpen,
  FileText,
  Download,
  Check,
  RotateCcw,
} from 'lucide-react';

import { API_URL } from '../../services/baseApi';

// ─── Zone de positionnement du code-barres ───────────────────────────────────

interface BarcodePosition {
  x: number; y: number; w: number; h: number;
}

interface CodeTextePosition {
  x: number; y: number;
}

function BarcodePositioner({
  imageUrl,
  position,
  onChange,
  codeTextePos,
  onCodeTexteChange,
  showCodeTexte,
  orientation,
}: {
  imageUrl: string;
  position: BarcodePosition;
  onChange: (pos: BarcodePosition) => void;
  codeTextePos: CodeTextePosition;
  onCodeTexteChange: (pos: CodeTextePosition) => void;
  showCodeTexte: boolean;
  orientation: 'portrait' | 'paysage';
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<'barcode' | 'texte' | null>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, origX: 0, origY: 0 });

  const handleMouseDown = useCallback((target: 'barcode' | 'texte') => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const origX = target === 'barcode' ? position.x : codeTextePos.x;
    const origY = target === 'barcode' ? position.y : codeTextePos.y;
    setDragging(target);
    setDragStart({ x: e.clientX, y: e.clientY, origX, origY });
  }, [position, codeTextePos]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const dx = ((e.clientX - dragStart.x) / rect.width) * 100;
    const dy = ((e.clientY - dragStart.y) / rect.height) * 100;
    const newX = dragStart.origX + dx;
    const newY = dragStart.origY + dy;
    if (dragging === 'barcode') {
      onChange({
        ...position,
        x: Math.max(0, Math.min(100 - position.w, newX)),
        y: Math.max(0, Math.min(100 - position.h, newY)),
      });
    } else {
      onCodeTexteChange({
        x: Math.max(0, Math.min(95, newX)),
        y: Math.max(0, Math.min(95, newY)),
      });
    }
  }, [dragging, dragStart, position, onChange, onCodeTexteChange]);

  const handleMouseUp = useCallback(() => setDragging(null), []);

  const aspectRatio = orientation === 'paysage' ? '297/210' : '210/297';

  return (
    <div
      ref={containerRef}
      className="relative border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden cursor-crosshair select-none"
      style={{ aspectRatio }}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {imageUrl && (
        <img
          src={imageUrl}
          alt="Template copie"
          className="w-full h-full object-contain"
          draggable={false}
        />
      )}
      {!imageUrl && (
        <div className="absolute inset-0 flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
          <Loader2 size={20} className="animate-spin mr-2" /> Chargement de l'image...
        </div>
      )}
      {/* Zone code-barres */}
      <div
        className="absolute border-2 border-red-500 bg-red-500/20 cursor-move flex items-center justify-center"
        style={{
          left: `${position.x}%`,
          top: `${position.y}%`,
          width: `${position.w}%`,
          height: `${position.h}%`,
        }}
        onMouseDown={handleMouseDown('barcode')}
      >
        <span className="text-[6px] font-bold text-red-700 bg-white/80 px-0.5 rounded whitespace-nowrap">
          CODE-BARRES
        </span>
      </div>
      {/* Zone code texte */}
      {showCodeTexte && (
        <div
          className="absolute border-2 border-blue-500 bg-blue-500/20 cursor-move flex items-center justify-center"
          style={{
            left: `${codeTextePos.x}%`,
            top: `${codeTextePos.y}%`,
            width: '8%',
            height: '2.5%',
          }}
          onMouseDown={handleMouseDown('texte')}
        >
          <span className="text-[6px] font-bold text-blue-700 bg-white/80 px-0.5 rounded whitespace-nowrap">
            CODE TEXTE
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Composant principal ─────────────────────────────────────────────────────

export default function ImpressionCopies() {
  const { data: modeles = [], isLoading: loadingModeles } = useGetModelesCopiesQuery();
  const { data: lots = [] } = useGetLotsCopiesQuery();
  const [createModele] = useCreateModeleCopieMutation();
  const [updateModele] = useUpdateModeleCopieMutation();
  const [deleteModele] = useDeleteModeleCopieMutation();
  const [marquerImprime] = useMarquerLotImprimeMutation();
  const [marquerNonImprime] = useMarquerLotNonImprimeMutation();
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // State
  const [selectedModele, setSelectedModele] = useState<ModeleCopie | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newNom, setNewNom] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newOrientation, setNewOrientation] = useState<'portrait' | 'paysage'>('paysage');
  const [newFormatPage, setNewFormatPage] = useState<'A4' | 'A3'>('A3');
  const [newRectoVerso, setNewRectoVerso] = useState(true);
  const [newFile, setNewFile] = useState<File | null>(null);
  const [newVersoFile, setNewVersoFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewVersoUrl, setPreviewVersoUrl] = useState<string | null>(null);

  // Position du code-barres (defaults: sous "PARCOURS" sur la copie A3 paysage)
  const [barcodePos, setBarcodePos] = useState<BarcodePosition>({ x: 52.5, y: 39, w: 15, h: 5 });
  const [afficherCodeTexte, setAfficherCodeTexte] = useState(true);
  const [codeTextePos, setCodeTextePos] = useState<CodeTextePosition>({ x: 61, y: 41 });
  const [codeTexteTaille, setCodeTexteTaille] = useState(12);
  const [codeTextePolice, setCodeTextePolice] = useState('Helvetica');
  const [barcodeCouleur, setBarcodeCouleur] = useState('#000000');
  const [codeTexteCouleur, setCodeTexteCouleur] = useState('#000000');
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoadingModele = useRef(false);

  // Génération PDF
  const [selectedLotId, setSelectedLotId] = useState<number | null>(null);
  const [customCodes, setCustomCodes] = useState('');
  const [codeSource, setCodeSource] = useState<'lot' | 'custom'>('lot');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const versoFileInputRef = useRef<HTMLInputElement>(null);

  // Charger les codes d'un lot sélectionné
  const selectedLot = lots.find(l => l.id === selectedLotId);
  const { data: lotCopies = [] } = useGetCopiesExamensQuery(
    selectedLotId ? { lot_id: selectedLotId } : undefined,
    { skip: !selectedLotId || codeSource !== 'lot' }
  );

  // Sélectionner un modèle
  const handleSelectModele = (modele: ModeleCopie) => {
    isLoadingModele.current = true;
    setSelectedModele(modele);
    setBarcodePos({
      x: modele.barcode_x,
      y: modele.barcode_y,
      w: modele.barcode_largeur,
      h: modele.barcode_hauteur,
    });
    setAfficherCodeTexte(modele.afficher_code_texte);
    setBarcodeCouleur(modele.barcode_couleur || '#000000');
    setCodeTexteCouleur(modele.code_texte_couleur || '#000000');
    setCodeTextePos({
      x: modele.code_texte_x ?? 61,
      y: modele.code_texte_y ?? 41,
    });
    setCodeTexteTaille(modele.code_texte_taille);
    setCodeTextePolice(modele.code_texte_police || 'Helvetica');
    setAutoSaveStatus('idle');
    // Remettre le flag après que les states soient mis à jour
    setTimeout(() => { isLoadingModele.current = false; }, 100);
  };

  // Upload fichier recto
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setNewFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  // Upload fichier verso
  const handleVersoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setNewVersoFile(file);
      setPreviewVersoUrl(URL.createObjectURL(file));
    }
  };

  // Créer un modèle
  const handleCreate = async () => {
    if (!newFile || !newNom.trim()) return;
    const fd = new FormData();
    fd.append('nom', newNom.trim());
    fd.append('description', newDescription.trim());
    fd.append('image_template', newFile);
    if (newVersoFile && newRectoVerso) {
      fd.append('image_verso', newVersoFile);
    }
    fd.append('orientation', newOrientation);
    fd.append('format_page', newFormatPage);
    fd.append('recto_verso', String(newRectoVerso));
    fd.append('barcode_x', String(barcodePos.x));
    fd.append('barcode_y', String(barcodePos.y));
    fd.append('barcode_largeur', String(barcodePos.w));
    fd.append('barcode_hauteur', String(barcodePos.h));
    fd.append('afficher_code_texte', String(afficherCodeTexte));
    fd.append('code_texte_x', String(codeTextePos.x));
    fd.append('code_texte_y', String(codeTextePos.y));
    fd.append('code_texte_taille', String(codeTexteTaille));
    fd.append('code_texte_police', codeTextePolice);
    fd.append('barcode_couleur', barcodeCouleur);
    fd.append('code_texte_couleur', codeTexteCouleur);
    try {
      const result = await createModele(fd).unwrap();
      setShowCreate(false);
      setNewFile(null);
      setNewVersoFile(null);
      setPreviewUrl(null);
      setPreviewVersoUrl(null);
      setNewNom('');
      setNewDescription('');
      handleSelectModele(result);
      setMessage({ type: 'success', text: 'Modele cree avec succes' });
    } catch {
      setMessage({ type: 'error', text: 'Erreur lors de la creation du modele' });
    }
  };

  // Sauvegarder la position (appelé manuellement ou par auto-save)
  const savePosition = useCallback(async (silent = false) => {
    if (!selectedModele) return;
    if (!silent) setAutoSaveStatus('saving');
    try {
      await updateModele({
        id: selectedModele.id,
        data: {
          barcode_x: barcodePos.x,
          barcode_y: barcodePos.y,
          barcode_largeur: barcodePos.w,
          barcode_hauteur: barcodePos.h,
          afficher_code_texte: afficherCodeTexte,
          code_texte_x: codeTextePos.x,
          code_texte_y: codeTextePos.y,
          code_texte_taille: codeTexteTaille,
          code_texte_police: codeTextePolice,
          barcode_couleur: barcodeCouleur,
          code_texte_couleur: codeTexteCouleur,
        },
      }).unwrap();
      setAutoSaveStatus('saved');
      if (!silent) setMessage({ type: 'success', text: 'Position sauvegardee' });
    } catch {
      setAutoSaveStatus('error');
      if (!silent) setMessage({ type: 'error', text: 'Erreur de sauvegarde' });
    }
  }, [selectedModele, barcodePos, afficherCodeTexte, codeTextePos, codeTexteTaille, codeTextePolice, barcodeCouleur, codeTexteCouleur, updateModele]);

  // Auto-save debounce : sauvegarde automatiquement 800ms apres le dernier changement
  useEffect(() => {
    if (!selectedModele || isLoadingModele.current) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setAutoSaveStatus('idle');
    autoSaveTimerRef.current = setTimeout(() => {
      savePosition(true);
    }, 800);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barcodePos, codeTextePos, codeTexteTaille, codeTextePolice, afficherCodeTexte, barcodeCouleur, codeTexteCouleur]);

  // Supprimer
  const handleDelete = async (id: number) => {
    if (!window.confirm('Supprimer ce modele ?')) return;
    await deleteModele(id);
    if (selectedModele?.id === id) setSelectedModele(null);
  };

  // Obtenir les codes pour la génération
  const getCodesForGeneration = (): string[] => {
    if (codeSource === 'lot' && selectedLotId) {
      // Utiliser les codes des copies du lot
      if (selectedLot?.codes_generes && selectedLot.codes_generes.length > 0) {
        return selectedLot.codes_generes;
      }
      // Fallback: utiliser les codes depuis la requête copies
      return lotCopies.map(c => c.code_copie);
    } else if (codeSource === 'custom') {
      return customCodes.split('\n').map(c => c.trim()).filter(Boolean);
    }
    return [];
  };

  // Générer PDF et ouvrir dans le navigateur pour impression directe
  const handleGenererPDF = async () => {
    if (!selectedModele) return;

    const codes = getCodesForGeneration();

    if (codes.length === 0) {
      setMessage({ type: 'error', text: 'Aucun code a generer. Selectionnez un lot ou saisissez des codes.' });
      return;
    }

    setGeneratingPDF(true);
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
      const resp = await fetch(
        `${API_URL}/modeles-copies/${selectedModele.id}/generer_pdf/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ codes }),
        }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setMessage({ type: 'success', text: `PDF genere avec ${codes.length} copies. Utilisez Ctrl+P dans l'onglet ouvert pour imprimer.` });
    } catch {
      setMessage({ type: 'error', text: 'Erreur lors de la generation du PDF' });
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Preview (fetch avec auth puis ouvrir le blob PDF)
  const [previewLoading, setPreviewLoading] = useState(false);
  const handlePreview = async () => {
    if (!selectedModele) return;
    setPreviewLoading(true);
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
      const resp = await fetch(
        `${API_URL}/modeles-copies/${selectedModele.id}/preview/?code=EX-TEST-001`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      setMessage({ type: 'error', text: 'Erreur lors de la preview' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const BACKEND_URL = API_URL.replace('/api', '');
  const getImageUrl = (path: string | null) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    if (path.startsWith('/')) return `${BACKEND_URL}${path}`;
    return `${BACKEND_URL}/media/${path}`;
  };

  // Charger l'image template via fetch (meme methode que le preview qui fonctionne)
  const [templateBlobUrl, setTemplateBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedModele?.image_template) {
      setTemplateBlobUrl(null);
      return;
    }
    let cancelled = false;
    const url = getImageUrl(selectedModele.image_template);
    fetch(url)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then(blob => {
        if (!cancelled) setTemplateBlobUrl(URL.createObjectURL(blob));
      })
      .catch(err => console.error('Failed to load template image:', url, err));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModele?.id, selectedModele?.image_template]);

  const codesCount = getCodesForGeneration().length;

  return (
    <div className="space-y-4">
      {/* Message */}
      {message && (
        <div className={`p-3 rounded-lg flex items-center gap-2 text-sm ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800/40'
            : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800/40'
        }`}>
          {message.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto"><X size={14} /></button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* COLONNE GAUCHE : Liste des modeles */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-800 dark:text-gray-200">Modeles de copies</h3>
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
            >
              <Plus size={14} /> Nouveau
            </button>
          </div>

          {loadingModeles ? (
            <div className="text-center py-8 text-gray-500">Chargement...</div>
          ) : modeles.length === 0 ? (
            <div className="text-center py-8 text-gray-400 dark:text-gray-500">
              <FileImage size={40} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">Aucun modele.</p>
              <p className="text-xs mt-1">Exportez votre copie Publisher en PNG, puis creez un modele.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {modeles.filter(m => m.est_actif).map((modele) => (
                <div
                  key={modele.id}
                  onClick={() => handleSelectModele(modele)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedModele?.id === modele.id
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 dark:border-indigo-600'
                      : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-gray-800 dark:text-gray-200 truncate">{modele.nom}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                          {modele.format_page}
                        </span>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                          {modele.orientation === 'paysage' ? 'Paysage' : 'Portrait'}
                        </span>
                        {modele.recto_verso && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                            <BookOpen size={9} /> R/V
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                        {new Date(modele.date_creation).toLocaleDateString('fr-FR')}
                        {modele.cree_par_nom && ` par ${modele.cree_par_nom}`}
                      </p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(modele.id); }}
                      className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 shrink-0"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Instructions rapides */}
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-lg">
            <p className="text-xs font-medium text-amber-800 dark:text-amber-300 mb-1">Comment faire ?</p>
            <ol className="text-[11px] text-amber-700 dark:text-amber-400 space-y-0.5 list-decimal list-inside">
              <li>Exportez votre copie Publisher en PNG (recto + verso)</li>
              <li>Creez un modele et uploadez les images</li>
              <li>Positionnez la zone du code-barres</li>
              <li>Selectionnez un lot et generez le PDF</li>
              <li>Imprimez le PDF (A3 recto-verso)</li>
            </ol>
          </div>
        </div>

        {/* COLONNE CENTRE : Positionnement */}
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1">
            <Move size={16} />
            Positionnement du code-barres
          </h3>

          {selectedModele ? (
            <>
              <BarcodePositioner
                imageUrl={templateBlobUrl || ''}
                position={barcodePos}
                onChange={setBarcodePos}
                codeTextePos={codeTextePos}
                onCodeTexteChange={setCodeTextePos}
                showCodeTexte={afficherCodeTexte}
                orientation={selectedModele.orientation}
              />

              {/* Legende */}
              <div className="flex gap-3 text-[10px]">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-2 border border-red-500 bg-red-500/30 rounded-sm" /> Code-barres (deplacez en glissant)
                </span>
                {afficherCodeTexte && (
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-2 border border-blue-500 bg-blue-500/30 rounded-sm" /> Code en clair
                  </span>
                )}
              </div>

              {/* Controles de position */}
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: 'X (%)', key: 'x' as const, min: 0, max: 100, step: 0.1 },
                  { label: 'Y (%)', key: 'y' as const, min: 0, max: 100, step: 0.1 },
                  { label: 'Largeur (%)', key: 'w' as const, min: 1, max: 50, step: 0.1 },
                  { label: 'Hauteur (%)', key: 'h' as const, min: 1, max: 20, step: 0.1 },
                ].map(({ label, key, min, max, step }) => (
                  <div key={key}>
                    <label className="text-xs text-gray-500 dark:text-gray-400">{label}</label>
                    <input
                      type="number" step={step} min={min} max={max}
                      value={barcodePos[key].toFixed(1)}
                      onChange={(e) => setBarcodePos({ ...barcodePos, [key]: +e.target.value })}
                      className="w-full px-2 py-1 text-xs border rounded dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                    />
                  </div>
                ))}
              </div>

              {/* Code texte controls */}
              <div className="space-y-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800/40">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={afficherCodeTexte}
                    onChange={(e) => setAfficherCodeTexte(e.target.checked)}
                    className="w-3.5 h-3.5 accent-blue-600 rounded"
                  />
                  <span className="text-xs font-medium text-blue-800 dark:text-blue-300">
                    Afficher le code en clair
                  </span>
                </label>
                {afficherCodeTexte && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-[10px] text-blue-600 dark:text-blue-400">X (%)</label>
                        <input
                          type="number" step="0.1" min="0" max="100"
                          value={codeTextePos.x.toFixed(1)}
                          onChange={(e) => setCodeTextePos({ ...codeTextePos, x: +e.target.value })}
                          className="w-full px-2 py-1 text-xs border rounded dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-blue-600 dark:text-blue-400">Y (%)</label>
                        <input
                          type="number" step="0.1" min="0" max="100"
                          value={codeTextePos.y.toFixed(1)}
                          onChange={(e) => setCodeTextePos({ ...codeTextePos, y: +e.target.value })}
                          className="w-full px-2 py-1 text-xs border rounded dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-blue-600 dark:text-blue-400">Taille (pt)</label>
                        <input
                          type="number" step="0.5" min="6" max="24"
                          value={codeTexteTaille}
                          onChange={(e) => setCodeTexteTaille(+e.target.value)}
                          className="w-full px-2 py-1 text-xs border rounded dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] text-blue-600 dark:text-blue-400">Police</label>
                      <select
                        value={codeTextePolice}
                        onChange={(e) => setCodeTextePolice(e.target.value)}
                        className="w-full px-2 py-1 text-xs border rounded dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                      >
                        <option value="Helvetica">Helvetica</option>
                        <option value="Helvetica-Bold">Helvetica Gras</option>
                        <option value="Courier">Courier</option>
                        <option value="Courier-Bold">Courier Gras</option>
                        <option value="Times-Roman">Times Roman</option>
                        <option value="Times-Bold">Times Gras</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Couleurs */}
              <div className="space-y-2 p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800/40">
                <span className="text-xs font-medium text-purple-800 dark:text-purple-300">Couleurs</span>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-purple-600 dark:text-purple-400">Code-barres</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={barcodeCouleur}
                        onChange={(e) => setBarcodeCouleur(e.target.value)}
                        className="w-8 h-8 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={barcodeCouleur}
                        onChange={(e) => setBarcodeCouleur(e.target.value)}
                        className="flex-1 px-2 py-1 text-xs font-mono border rounded dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] text-purple-600 dark:text-purple-400">Code en clair</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={codeTexteCouleur}
                        onChange={(e) => setCodeTexteCouleur(e.target.value)}
                        className="w-8 h-8 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={codeTexteCouleur}
                        onChange={(e) => setCodeTexteCouleur(e.target.value)}
                        className="flex-1 px-2 py-1 text-xs font-mono border rounded dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Info modele + auto-save status */}
              <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded p-2 space-y-0.5">
                <p><strong>Format :</strong> {selectedModele.format_page} {selectedModele.orientation}</p>
                <p><strong>Recto-verso :</strong> {selectedModele.recto_verso ? 'Oui' : 'Non'}</p>
                {selectedModele.image_verso && <p><strong>Image verso :</strong> Presente</p>}
                <p className="flex items-center gap-1">
                  <strong>Position :</strong>
                  {autoSaveStatus === 'saving' && <span className="text-amber-500 dark:text-amber-400 flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Sauvegarde...</span>}
                  {autoSaveStatus === 'saved' && <span className="text-green-600 dark:text-green-400 flex items-center gap-1"><CheckCircle2 size={10} /> Sauvegardee</span>}
                  {autoSaveStatus === 'error' && <span className="text-red-500 dark:text-red-400 flex items-center gap-1"><AlertCircle size={10} /> Erreur</span>}
                  {autoSaveStatus === 'idle' && <span className="text-gray-400">-</span>}
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => savePosition(false)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                >
                  <Settings size={14} /> Sauvegarder position
                </button>
                <button
                  onClick={handlePreview}
                  className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                >
                  <Eye size={14} /> Preview
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
              <FileImage size={32} className="text-gray-300 dark:text-gray-600 mb-2" />
              <p className="text-gray-400 dark:text-gray-500 text-sm">Selectionnez un modele a gauche</p>
            </div>
          )}
        </div>

        {/* COLONNE DROITE : Generation PDF */}
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1">
            <Printer size={16} />
            Generer les copies PDF
          </h3>

          {selectedModele ? (
            <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              {/* Source des codes */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Source des codes</label>
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => setCodeSource('lot')}
                    className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                      codeSource === 'lot'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    <FileText size={14} className="inline mr-1" />
                    Depuis un lot
                  </button>
                  <button
                    onClick={() => setCodeSource('custom')}
                    className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                      codeSource === 'custom'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    Codes manuels
                  </button>
                </div>
              </div>

              {codeSource === 'lot' ? (
                <div>
                  <label className="text-sm text-gray-600 dark:text-gray-400">Selectionnez un lot</label>
                  <select
                    value={selectedLotId || ''}
                    onChange={(e) => setSelectedLotId(e.target.value ? +e.target.value : null)}
                    className="w-full mt-1 px-3 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                  >
                    <option value="">-- Choisir un lot --</option>
                    {lots.map((lot) => (
                      <option key={lot.id} value={lot.id}>
                        {lot.est_imprime ? '\u2713 ' : ''}{lot.code_lot} ({lot.nombre_copies} copies){lot.est_imprime ? ' - Imprime' : ''}
                      </option>
                    ))}
                  </select>
                  {selectedLotId && selectedLot && (
                    <div className="mt-2 space-y-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {codesCount > 0 ? (
                          <span className="text-green-600 dark:text-green-400">{codesCount} codes disponibles</span>
                        ) : (
                          <span className="text-amber-600 dark:text-amber-400">Chargement des codes...</span>
                        )}
                      </p>
                      {/* Statut d'impression du lot */}
                      <div className={`p-2 rounded-lg border text-xs flex items-center justify-between ${
                        selectedLot.est_imprime
                          ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800/40'
                          : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700'
                      }`}>
                        <div>
                          {selectedLot.est_imprime ? (
                            <span className="text-green-700 dark:text-green-400 flex items-center gap-1">
                              <CheckCircle2 size={12} /> Lot imprime
                              {selectedLot.date_impression && (
                                <span className="text-green-600/70 dark:text-green-500/70 ml-1">
                                  le {new Date(selectedLot.date_impression).toLocaleDateString('fr-FR')}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-gray-500 dark:text-gray-400">Non imprime</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {selectedLot.est_imprime ? (
                            <button
                              onClick={() => marquerNonImprime(selectedLot.id)}
                              className="flex items-center gap-1 px-2 py-1 text-[10px] bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                              title="Remettre comme non imprime"
                            >
                              <RotateCcw size={10} /> Annuler
                            </button>
                          ) : (
                            <button
                              onClick={() => marquerImprime({ lotId: selectedLot.id })}
                              className="flex items-center gap-1 px-2 py-1 text-[10px] bg-green-600 text-white rounded hover:bg-green-700"
                              title="Marquer manuellement comme imprime"
                            >
                              <Check size={10} /> Marquer imprime
                            </button>
                          )}
                          {selectedLot.fichier_pdf && (
                            <button
                              onClick={async () => {
                                const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
                                const resp = await fetch(
                                  `${API_URL}/lots-copies/${selectedLot.id}/telecharger_pdf/`,
                                  { headers: token ? { Authorization: `Bearer ${token}` } : {} }
                                );
                                if (resp.ok) {
                                  const blob = await resp.blob();
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `${selectedLot.code_lot}_copies.pdf`;
                                  a.click();
                                  URL.revokeObjectURL(url);
                                }
                              }}
                              className="flex items-center gap-1 px-2 py-1 text-[10px] bg-blue-600 text-white rounded hover:bg-blue-700"
                              title="Telecharger le PDF"
                            >
                              <Download size={10} /> PDF
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <label className="text-sm text-gray-600 dark:text-gray-400">
                    Codes (un par ligne)
                  </label>
                  <textarea
                    value={customCodes}
                    onChange={(e) => setCustomCodes(e.target.value)}
                    rows={6}
                    placeholder={"EX1\nEX2\nEX3\n..."}
                    className="w-full mt-1 px-3 py-2 border rounded-lg text-sm font-mono dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {customCodes.split('\n').filter(c => c.trim()).length} codes
                  </p>
                </div>
              )}

              {/* Resume */}
              <div className="p-3 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 space-y-1">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Modele :</strong> {selectedModele.nom}
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Format :</strong> {selectedModele.format_page} {selectedModele.orientation}
                  {selectedModele.recto_verso && ' (recto-verso)'}
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Codes :</strong> {codesCount}
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Pages PDF :</strong> {codesCount}{selectedModele.recto_verso ? ` (${codesCount * 2} faces)` : ''}
                </p>
              </div>

              <button
                onClick={handleGenererPDF}
                disabled={generatingPDF || codesCount === 0}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {generatingPDF ? (
                  <><Loader2 size={18} className="animate-spin" /> Generation en cours...</>
                ) : (
                  <><Printer size={18} /> Generer et imprimer</>
                )}
              </button>

              {codesCount === 0 && selectedLotId && codeSource === 'lot' && (
                <p className="text-xs text-amber-600 dark:text-amber-400 text-center">
                  Aucun code trouve dans ce lot. Verifiez que le lot contient des copies.
                </p>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-40 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <Printer size={24} className="text-gray-300 dark:text-gray-600 mb-2" />
              <p className="text-gray-400 dark:text-gray-500 text-sm">Selectionnez un modele</p>
            </div>
          )}
        </div>
      </div>

      {/* MODAL CREATION */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100">Nouveau modele de copie</h3>
              <button
                onClick={() => { setShowCreate(false); setNewFile(null); setNewVersoFile(null); setPreviewUrl(null); setPreviewVersoUrl(null); }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Nom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nom du modele <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newNom}
                  onChange={(e) => setNewNom(e.target.value)}
                  placeholder="Ex: Copie Examen S1 2025-2026"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <input
                  type="text"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder="Ex: Copie A3 paysage recto-verso pour examens normaux"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                />
              </div>

              {/* Format et orientation */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Format de page</label>
                  <div className="flex gap-2">
                    {(['A3', 'A4'] as const).map(fmt => (
                      <button
                        key={fmt}
                        onClick={() => setNewFormatPage(fmt)}
                        className={`flex-1 py-2 rounded text-sm font-medium transition-colors ${
                          newFormatPage === fmt
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {fmt}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Orientation</label>
                  <div className="flex gap-2">
                    {([
                      { value: 'paysage' as const, label: 'Paysage' },
                      { value: 'portrait' as const, label: 'Portrait' },
                    ]).map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setNewOrientation(opt.value)}
                        className={`flex-1 py-2 rounded text-sm font-medium transition-colors ${
                          newOrientation === opt.value
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Recto-verso */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newRectoVerso}
                    onChange={(e) => setNewRectoVerso(e.target.checked)}
                    className="w-4 h-4 accent-indigo-600 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Impression recto-verso
                  </span>
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 ml-6">
                  Si active, uploadez aussi l'image du verso ci-dessous
                </p>
              </div>

              {/* Image recto */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Image recto (PNG/JPG) <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Exportez la page recto de votre copie Publisher en image PNG
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg w-full justify-center hover:border-indigo-400 dark:border-gray-600 dark:hover:border-indigo-500 text-gray-600 dark:text-gray-400"
                >
                  <Upload size={18} />
                  {newFile ? newFile.name : 'Choisir l\'image recto...'}
                </button>
                {previewUrl && (
                  <div className="mt-2 border rounded-lg overflow-hidden dark:border-gray-600" style={{ maxHeight: '150px' }}>
                    <img src={previewUrl} alt="Preview recto" className="w-full object-contain" />
                  </div>
                )}
              </div>

              {/* Image verso (conditionnel) */}
              {newRectoVerso && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Image verso (PNG/JPG)
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Exportez la page verso de votre copie Publisher en image PNG
                  </p>
                  <input
                    ref={versoFileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={handleVersoFileChange}
                    className="hidden"
                  />
                  <button
                    onClick={() => versoFileInputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg w-full justify-center hover:border-indigo-400 dark:border-gray-600 dark:hover:border-indigo-500 text-gray-600 dark:text-gray-400"
                  >
                    <Upload size={18} />
                    {newVersoFile ? newVersoFile.name : 'Choisir l\'image verso...'}
                  </button>
                  {previewVersoUrl && (
                    <div className="mt-2 border rounded-lg overflow-hidden dark:border-gray-600" style={{ maxHeight: '150px' }}>
                      <img src={previewVersoUrl} alt="Preview verso" className="w-full object-contain" />
                    </div>
                  )}
                </div>
              )}

              {/* Boutons */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => { setShowCreate(false); setNewFile(null); setNewVersoFile(null); setPreviewUrl(null); setPreviewVersoUrl(null); }}
                  className="flex-1 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreate}
                  disabled={!newFile || !newNom.trim()}
                  className="flex-1 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Creer le modele
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
